import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Edit, Building2, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { logoColor as LOGO_URL } from "@/lib/logos";

const emptyDir = () => ({ nombre: "", direccion: "", comuna: "", ciudad: "Santiago", correo: "" });

export default function AdminClientes() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", rut: "", contacto_nombre: "", contacto_email: "", contacto_telefono: "", direcciones: [emptyDir()] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try { const r = await api.get("/api/clients"); setClients(r.data); } catch {}
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`¿Eliminar cliente "${client.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/api/clients/${client.id}`);
      toast.success("Cliente eliminado");
      loadClients();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  


  const uf = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const udir = (i, f, v) => setForm((p) => {
    const d = [...p.direcciones]; d[i] = { ...d[i], [f]: v }; return { ...p, direcciones: d };
  });
  const addDir = () => setForm((p) => ({ ...p, direcciones: [...p.direcciones, emptyDir()] }));
  const removeDir = (i) => setForm((p) => ({ ...p, direcciones: p.direcciones.filter((_, idx) => idx !== i) }));

  const openNew = () => { setEditing(null); setForm({ nombre: "", rut: "", contacto_nombre: "", contacto_email: "", contacto_telefono: "", direcciones: [emptyDir()] }); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ nombre: c.nombre, rut: c.rut || "", contacto_nombre: c.contacto_nombre || "", contacto_email: c.contacto_email || "", contacto_telefono: c.contacto_telefono || "",
      direcciones: c.direcciones?.length > 0 ? c.direcciones : [emptyDir()] });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre) { toast.error("Nombre requerido"); return; }
    if (form.direcciones.some((d) => !d.nombre || !d.direccion)) { toast.error("Completa todas las direcciones"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/clients/${editing.id}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/api/clients", form);
        toast.success("Cliente creado");
      }
      setShowForm(false);
      loadClients();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">Gestión de Clientes</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#0FB4B7] text-white" onClick={openNew}><Plus className="h-3.5 w-3.5 mr-1" /> Nuevo cliente</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}><ArrowLeft className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-2">
        {clients.map((c) => (
          <Card key={c.id} className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#0FB4B7]" />
                  <span className="text-sm font-medium">{c.nombre}</span>
                  <Badge className="bg-emerald-50 text-emerald-700 border-0">{c.estado || "activo"}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteClient(c)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <p className="text-xs text-gray-500">{c.contacto_nombre} · {c.contacto_email}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                {c.direcciones?.map((d, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-gray-100 rounded-full text-gray-500 flex items-center gap-1">
                    <MapPin className="h-2.5 w-2.5" /> {d.nombre}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </main>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#384091]">{editing ? "Editar" : "Nuevo"} Cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre / Razón Social *</Label><Input value={form.nombre} onChange={(e) => uf("nombre", e.target.value)} className="mt-1" /></div>
            <div><Label>RUT</Label><Input value={form.rut} onChange={(e) => uf("rut", e.target.value)} className="mt-1" placeholder="76.543.210-K" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Contacto</Label><Input value={form.contacto_nombre} onChange={(e) => uf("contacto_nombre", e.target.value)} className="mt-1" /></div>
              <div><Label>Email</Label><Input value={form.contacto_email} onChange={(e) => uf("contacto_email", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>Teléfono</Label><Input value={form.contacto_telefono} onChange={(e) => uf("contacto_telefono", e.target.value)} className="mt-1" /></div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#384091] font-semibold">Direcciones</Label>
                <Button variant="outline" size="sm" onClick={addDir}><Plus className="h-3 w-3 mr-1" /> Agregar</Button>
              </div>
              {form.direcciones.map((d, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 mb-2 relative">
                  <p className="text-[10px] font-semibold text-gray-400 mb-2">Dirección {i + 1}</p>
                  {form.direcciones.length > 1 && (
                    <button onClick={() => removeDir(i)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                  <div className="space-y-2">
                    <Input placeholder="Nombre sucursal *" value={d.nombre} onChange={(e) => udir(i, "nombre", e.target.value)} className="h-8 text-xs" />
                    <Input placeholder="Dirección *" value={d.direccion} onChange={(e) => udir(i, "direccion", e.target.value)} className="h-8 text-xs" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Comuna" value={d.comuna} onChange={(e) => udir(i, "comuna", e.target.value)} className="h-8 text-xs" />
                      <Input placeholder="Ciudad" value={d.ciudad} onChange={(e) => udir(i, "ciudad", e.target.value)} className="h-8 text-xs" />
                    </div>
                    <Input type="email" placeholder="Correo sucursal (para envío MC)" value={d.correo || ""} onChange={(e) => udir(i, "correo", e.target.value)} className="h-8 text-xs" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button className="bg-[#0FB4B7] text-white" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : editing ? "Actualizar" : "Crear cliente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
